'use client';

// Design App fork — client side of the per-user agent-app auth gate.
//
// The host (x-ui chat browser) launches us as an agent-app: it mints a launch
// token and delivers it via the iframe URL hash (#launchToken=…) and/or a
// postMessage after we announce readiness. We trade that token at the daemon
// (`POST /api/session/exchange`), which sets the signed `x_agent_app_session`
// cookie. From then on the daemon scopes every project list/read to this user,
// so each user only ever sees their own designs. Mirrors the 42bucks
// default-app / QA-app handshake.

const LAUNCH_TOKEN_TYPE = 'x-agent-app:launch-token';
const AUTH_READY_TYPE = 'x-agent-app:auth-ready';
const BRIDGE_VERSION = 1;
const APP_ID = 'design';
const AUTH_READY_RETRY_MS = 400;
const AUTH_READY_TIMEOUT_MS = 12000;

// Only accept the launch token from a trusted 42bucks host origin embedding us
// (defence in depth — the token itself is also validated server-side at exchange,
// but we never want to act on a message from an arbitrary page that iframes us).
// Override per-deployment (white-label brand chat domains) via the build-time env.
const DEFAULT_HOST_ORIGINS = ['https://app.42bucks.com'];
const TRUSTED_HOST_ORIGINS: ReadonlySet<string> = new Set(
  (process.env.NEXT_PUBLIC_AGENT_APP_HOST_ORIGINS ?? '')
    .split(',')
    .map((origin) => origin.trim())
    .filter(Boolean)
    .concat(DEFAULT_HOST_ORIGINS),
);

function readLaunchTokenFromLocation(): string | null {
  try {
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const query = new URLSearchParams(window.location.search);
    return (
      hash.get('launchToken') ||
      hash.get('launch_token') ||
      query.get('launchToken') ||
      query.get('launch_token')
    );
  } catch {
    return null;
  }
}

function stripLaunchTokenFromUrl(): void {
  try {
    if (/launch_?token/i.test(window.location.hash)) {
      window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
    }
  } catch {
    /* noop */
  }
}

interface SessionIdentity {
  userId: string;
  workspaceId: string;
}

/**
 * Current session identity as the daemon sees it, or null when unauthenticated.
 * Used both as the plain "do we have a session" check and, when embedded, as
 * the baseline a later host-posted token gets compared against.
 */
async function currentSession(): Promise<SessionIdentity | null> {
  try {
    const res = await fetch('/api/session', { credentials: 'include' });
    if (!res.ok) return null;
    const json = (await res.json()) as {
      authenticated?: boolean;
      session?: { user?: { id?: string }; workspaceId?: string };
    };
    if (!json?.authenticated) return null;
    const userId = json.session?.user?.id;
    const workspaceId = json.session?.workspaceId;
    if (!userId || !workspaceId) return null;
    return { userId, workspaceId };
  } catch {
    return null;
  }
}

interface ExchangeResult {
  ok: boolean;
  userId?: string;
  workspaceId?: string;
}

// In-flight dedupe only — NOT a "never exchange again" latch. The launch
// handshake can retry-announce and receive more than one postMessage in
// flight; this collapses concurrent calls onto one request without
// preventing a later, distinct exchange (e.g. a reconciling token after an
// earlier one failed).
let inFlightExchange: Promise<ExchangeResult> | null = null;

async function performExchange(launchToken: string): Promise<ExchangeResult> {
  try {
    const res = await fetch('/api/session/exchange', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ launchToken }),
    });
    if (!res.ok) return { ok: false };
    const json = (await res.json()) as {
      session?: { user?: { id?: string }; workspaceId?: string };
    };
    return {
      ok: true,
      userId: json?.session?.user?.id,
      workspaceId: json?.session?.workspaceId,
    };
  } catch {
    return { ok: false };
  }
}

async function exchange(launchToken: string): Promise<ExchangeResult> {
  if (inFlightExchange) return inFlightExchange;
  const pending = performExchange(launchToken).finally(() => {
    inFlightExchange = null;
  });
  inFlightExchange = pending;
  return pending;
}

function readTokenFromMessage(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const payload = data as { type?: unknown; version?: unknown; token?: unknown; expiresAt?: unknown };
  if (payload.type !== LAUNCH_TOKEN_TYPE || payload.version !== BRIDGE_VERSION) return null;
  if (typeof payload.token !== 'string' || payload.token.length === 0) return null;
  if (typeof payload.expiresAt === 'string' && Date.parse(payload.expiresAt) <= Date.now()) return null;
  return payload.token;
}

/**
 * Resolves once the app is authenticated, or once we've given up (standalone
 * load, or the launch handshake timed out). The caller gates rendering on this
 * so the project list/read fetches always run with the session cookie present.
 */
export async function bootstrapAgentAppAuth(): Promise<void> {
  if (typeof window === 'undefined') return;

  // A freshly delivered launch token ALWAYS wins over an existing cookie —
  // the host only mints one when it wants THIS identity in the frame. Trading
  // it in unconditionally, before ever asking whether a session cookie is
  // already present, is what closes the identity leak: without this, a
  // second user/workspace opening the same embedded frame within the
  // cookie's TTL would short-circuit on the existing session check and
  // silently inherit whoever the previous occupant was.
  const urlToken = readLaunchTokenFromLocation();
  if (urlToken) {
    const result = await exchange(urlToken);
    stripLaunchTokenFromUrl();
    if (result.ok) return;
  }

  // No fresh token on the URL. An existing session is good enough to render
  // with — but we still note its identity so the embedded listener below can
  // detect a stale-vs-fresh mismatch if the host posts a token later.
  const existing = await currentSession();

  // Not embedded → no host to ask; render as-is (with or without a session).
  if (window.parent === window) return;

  // Embedded: announce readiness and exchange the launch token the host posts
  // back. Re-announce until a session lands or the bounded window elapses.
  await new Promise<void>((resolve) => {
    let settled = false;
    let done = false;
    let retry: ReturnType<typeof setInterval> | null = null;
    let deadline: ReturnType<typeof setTimeout> | null = null;

    const resolveOnce = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    const cleanup = () => {
      window.removeEventListener('message', onMessage);
      if (retry) clearInterval(retry);
      if (deadline) clearTimeout(deadline);
    };
    const finish = () => {
      if (done) return;
      done = true;
      cleanup();
      resolveOnce();
    };
    const announce = () => {
      try {
        window.parent.postMessage({ type: AUTH_READY_TYPE, version: BRIDGE_VERSION, appId: APP_ID }, '*');
      } catch {
        /* noop */
      }
    };
    async function onMessage(event: MessageEvent) {
      // Must come from our actual embedding parent, on a trusted host origin —
      // a malicious page that iframes us would be `window.parent` too, so the
      // origin allowlist is the real gate.
      if (event.source !== window.parent) return;
      if (!TRUSTED_HOST_ORIGINS.has(event.origin)) return;
      const token = readTokenFromMessage(event.data);
      if (!token) return;
      const result = await exchange(token);
      if (!result.ok) return;
      if (existing && (result.userId !== existing.userId || result.workspaceId !== existing.workspaceId)) {
        // The host handed us a token for a DIFFERENT identity than the
        // session we already rendered with. We cannot safely swap identities
        // under a live app instance client-side, so reload: the next
        // bootstrap pass sees this same token on next redeem (or the host
        // re-delivers) and the fix above takes it unconditionally.
        window.location.reload();
        return;
      }
      finish();
    }

    window.addEventListener('message', onMessage);
    announce();
    retry = setInterval(announce, AUTH_READY_RETRY_MS);
    deadline = setTimeout(finish, AUTH_READY_TIMEOUT_MS);

    if (existing) {
      // Don't block first paint on the handshake when we already have a
      // session to render with — resolve now, keep listening in the
      // background, and only reload if a later token reconciles to a
      // different identity.
      resolveOnce();
    }
  });
}
